import React, { useMemo, useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeUnits } from 'Core/hooks';
import { useIntl } from 'react-intl';
import { Slider, styled, IconButton, Tooltip } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import moment from 'moment-timezone';

import { createClipRequestFromDrawer } from '../ClipRequestDrawerMap/redux/slice';
import { getVidFleetEvent, getLightMetricEvent, getSwitnessEvent } from 'Core/data/VideoEvents';
import './style.scss';

const TIMELINE_VIDEO_ICON_SIZE = 20;

function TimelineEventIcon({ url, name, color }) {
    if (url) {
        return (
            <img
                src={url}
                alt={name || ''}
                className="trails-trip-timeline__video-icon-img"
                width={TIMELINE_VIDEO_ICON_SIZE}
                height={TIMELINE_VIDEO_ICON_SIZE}
            />
        );
    }
    return (
        <span
            className="trails-trip-timeline__video-icon-dot"
            style={{ backgroundColor: color || '#16aaf2' }}
            title={name}
        />
    );
}

// Colores para la línea de tiempo: driver = verde, idle = amarillo, stop = rojo
const SEGMENT_COLORS = {
    driver: '#00ad00',
    idle: '#c7be00',
    stop: '#b65345',
    other: '#d7d7d6',
};

const EVENT_TO_SEGMENT = {
    driver: ['Drive', 'Motion', 'Motion Start', 'Travel Start', 'Update', 'Poll B', 'Poll C'],
    idle: ['Idle', 'IDLING'],
    stop: [
        'Stop',
        'Ignition Off',
        'Main Power Off',
        'Parked',
        'Travel Stop',
        'Motion Stop',
        'Stop Sign Violation',
        'Sudden Stop',
    ],
};

const getSegmentType = (eventName) => {
    if (!eventName) return 'other';
    if (EVENT_TO_SEGMENT.driver.includes(eventName)) return 'driver';
    if (EVENT_TO_SEGMENT.idle.includes(eventName)) return 'idle';
    if (EVENT_TO_SEGMENT.stop.includes(eventName)) return 'stop';
    return 'other';
};

const getSegmentColor = (eventName) => SEGMENT_COLORS[getSegmentType(eventName)];

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const TWENTY_MINUTES_MS = 20 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_MINUTE_MS = 60 * 1000;
const MIN_RANGE_DURATION_MS = ONE_MINUTE_MS;
const MAX_RANGE_DURATION_MS = FIVE_MINUTES_MS;

/**
 * Genera marcas de tiempo según la duración total:
 * - Si el rango es más de 1 hora: marcas cada 20 min (menos etiquetas, evita solapamiento).
 * - Si el rango es 1 hora o menos: marcas cada 5 min.
 * Siempre incluye la hora inicial en 0% y luego cada intervalo hasta el fin.
 */
const getTimeScaleTicks = (startDate, endDate, totalMs) => {
    if (!startDate || !endDate || totalMs <= 0) return [];
    const intervalMs = totalMs > ONE_HOUR_MS ? TWENTY_MINUTES_MS : FIVE_MINUTES_MS;
    const startMs = new Date(startDate).getTime();
    const endMs = new Date(endDate).getTime();
    const ticks = [];
    let tickMs = startMs;
    while (tickMs <= endMs) {
        const percent = ((tickMs - startMs) / totalMs) * 100;
        ticks.push({ time: new Date(tickMs), percent });
        tickMs += intervalMs;
    }
    return ticks;
};

const TrailsTripTimelineSlider = styled(Slider)({
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '100%',
    maxWidth: '100%',
    margin: 0,
    padding: 0,
    height: 24,
    color: '#7b1fa2',
    boxSizing: 'border-box',
    '&.MuiSlider-root': {
        margin: 0,
        padding: 0,
    },
    '& .MuiSlider-rail': {
        opacity: 0,
    },
    '& .MuiSlider-track': {
        opacity: 0,
        border: 'none',
    },
    '& .MuiSlider-thumb': {
        width: 8,
        height: 20,
        marginLeft: 0,
        borderRadius: 2,
        backgroundColor: '#7b1fa2',
        border: '2px solid #fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        '&:focus, &:hover, &.Mui-active': {
            boxShadow: '0 0 0 8px rgba(123, 31, 162, 0.16)',
        },
        '&::before': {
            display: 'none',
        },
    },
});

/**
 * TrailsTripTimeline Component
 *
 * Línea de tiempo horizontal con segmentos por tipo de evento (driver/idle/stop)
 * y slider de rango para seleccionar un intervalo. Fecha inicial y final del primer
 * y último ítem; colores: verde = driver, amarillo = idle, rojo = stop.
 *
 * Rango seleccionado:
 * - onRangeChange({ startTime, endTime, percent }) se llama al cambiar y al cargar datos.
 * - ref.current.getSelectedTimeRange() devuelve { startTime, endTime, percent } en cualquier momento.
 */
const TrailsTripTimeline = forwardRef(function TrailsTripTimeline(
    { mapRef, trailsFilterEvents, onRangeChange },
    ref
) {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const vehicleTrails = useSelector(
        (state) => state.realtimeMapUtilitiesvehiclesTrailRedux.vehicleTrails
    );
    const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
    const events = vehicleTrails?.items ?? [];

    const unit = useMemo(() => {
        return units.find(unit => unit?.id == events[0].deviceId || unit?.videoDevices?.[0]?.id == events[0].deviceId);
    }, [units, events]);

    const { startDate, endDate, totalMs, segments } = useMemo(() => {
        if (!events.length) {
            return { startDate: null, endDate: null, totalMs: 0, segments: [] };
        }
        const sorted = [...events].sort(
            (a, b) => new Date(a.unitTime).getTime() - new Date(b.unitTime).getTime()
        );
        const start = new Date(sorted[0].unitTime).getTime();
        const end = new Date(sorted[sorted.length - 1].unitTime).getTime();
        const total = end - start || 1;

        const segs = [];
        for (let i = 0; i < sorted.length - 1; i++) {
            const t0 = new Date(sorted[i].unitTime).getTime();
            const t1 = new Date(sorted[i + 1].unitTime).getTime();
            segs.push({
                left: ((t0 - start) / total) * 100,
                width: ((t1 - t0) / total) * 100,
                color: getSegmentColor(sorted[i].eventName),
                event: sorted[i],
            });
        }
        return {
            startDate: sorted[0].unitTime,
            endDate: sorted[sorted.length - 1].unitTime,
            totalMs: total,
            segments: segs,
        };
    }, [events]);

    const timeScaleTicks = useMemo(
        () => getTimeScaleTicks(startDate, endDate, totalMs),
        [startDate, endDate, totalMs]
    );

    const videoEventsOnTimeline = useMemo(() => {
        if (!startDate || !endDate || totalMs <= 0 || !events.length) return [];
        const startMs = new Date(startDate).getTime();
        const result = [];
        events.forEach((ev) => {
            let eventData = null;
            if (ev?.eventCode?.includes?.('CameraEvent')) {
                eventData = getVidFleetEvent(ev.eventCode);
            } else if (ev?.unitType === 'switness') {
                eventData = getSwitnessEvent(ev.eventCode);
            } else {
                eventData = getLightMetricEvent(ev?.eventCode);
            }
            if (!eventData) return;
            if (eventData.name === 'Default') return;
            const icon = eventData.icon;
            const name = eventData.name || ev.eventName;
            const eventTime = new Date(ev.unitTime).getTime();
            const percent = ((eventTime - startMs) / totalMs) * 100;
            if (percent < 0 || percent > 100) return;
            result.push({
                id: ev.id,
                event: ev,
                percent,
                icon: icon || null,
                name,
                color: eventData.color,
            });
        });
        return result;
    }, [events, startDate, endDate, totalMs]);

    const vehicleTimezone = unit?.timeZone?.name;

    const formatTime = useCallback(
        (isoDate) => {
            if (!isoDate) return '';
            return vehicleTimezone
                ? moment(isoDate).tz(vehicleTimezone).format('hh:mm:ss A')
                : moment(isoDate).format('hh:mm:ss A');
        },
        [vehicleTimezone]
    );

    const formatScaleTime = useCallback(
        (date) => vehicleTimezone
            ? moment(date).tz(vehicleTimezone).format('hh:mm:ss')
            : moment(date).format('hh:mm:ss'),
        [vehicleTimezone]
    );

    const minMaxPercent = useMemo(() => {
        if (totalMs <= 0) return { minDurationPercent: 0, maxDurationPercent: 100 };
        const minPct = (MIN_RANGE_DURATION_MS / totalMs) * 100;
        const maxPct = (MAX_RANGE_DURATION_MS / totalMs) * 100;
        return {
            minDurationPercent: Math.min(100, minPct),
            maxDurationPercent: Math.min(100, maxPct),
        };
    }, [totalMs]);

    const [rangePercent, setRangePercent] = useState([0, 100]);

    const selectedTimeRange = useMemo(() => {
        if (!startDate || !endDate || totalMs <= 0) return null;
        const startMs = new Date(startDate).getTime();
        const [v0, v1] = rangePercent;
        return {
            startTime: new Date(startMs + (v0 / 100) * totalMs),
            endTime: new Date(startMs + (v1 / 100) * totalMs),
            percent: [v0, v1],
        };
    }, [startDate, endDate, totalMs, rangePercent]);

    const deviceId = unit?.videoDevices?.[0]?.id || unit?.id || null;

    const handleRequestClip = useCallback(() => {
        if (!selectedTimeRange || !deviceId) return;
        const dateRange = {
            start: moment(selectedTimeRange.startTime).utc().format(),
            end: moment(selectedTimeRange.endTime).utc().format(),
        };
        dispatch(createClipRequestFromDrawer({ deviceId, dateRange }));
    }, [selectedTimeRange, deviceId, dispatch]);

    useImperativeHandle(
        ref,
        () => ({
            getSelectedTimeRange: () => selectedTimeRange,
        }),
        [selectedTimeRange]
    );

    useEffect(() => {
        if (selectedTimeRange) {
            console.log('TrailsTripTimeline — rango del slider:', {
                startTime: selectedTimeRange.startTime.toISOString(),
                endTime: selectedTimeRange.endTime.toISOString(),
                percent: selectedTimeRange.percent,
            });
        }
    }, [selectedTimeRange]);

    useEffect(() => {
        const maxPct = totalMs > 0 ? Math.min(100, (MAX_RANGE_DURATION_MS / totalMs) * 100) : 100;
        const initialRange = [0, maxPct];
        setRangePercent(initialRange);
        if (onRangeChange && startDate && endDate && totalMs) {
            const startMs = new Date(startDate).getTime();
            onRangeChange({
                startTime: new Date(startMs),
                endTime: new Date(startMs + (maxPct / 100) * totalMs),
                percent: initialRange,
            });
        }
    }, [totalMs, startDate, endDate, onRangeChange]);

    const clampRange = useCallback(
        (newValue, prevValue) => {
            const { minDurationPercent, maxDurationPercent } = minMaxPercent;
            let [v0, v1] = newValue;
            const leftMoved = prevValue && newValue[0] !== prevValue[0];
            const rightMoved = prevValue && newValue[1] !== prevValue[1];
            if (leftMoved) {
                v0 = Math.max(0, Math.min(100 - minDurationPercent, v0));
                v1 = Math.max(v0 + minDurationPercent, Math.min(v0 + maxDurationPercent, v1));
                v1 = Math.min(100, v1);
            } else if (rightMoved) {
                v1 = Math.min(100, Math.max(minDurationPercent, v1));
                v0 = Math.max(v1 - maxDurationPercent, Math.min(v1 - minDurationPercent, v0));
                v0 = Math.max(0, v0);
            } else {
                const duration = v1 - v0;
                if (duration < minDurationPercent) {
                    v1 = Math.min(100, v0 + minDurationPercent);
                } else if (duration > maxDurationPercent) {
                    v1 = v0 + maxDurationPercent;
                    v1 = Math.min(100, v1);
                }
            }
            return [v0, v1];
        },
        [minMaxPercent]
    );

    const handleRangeChange = useCallback(
        (event, newValue) => {
            if (!Array.isArray(newValue)) return;
            const clamped = clampRange(newValue, rangePercent);
            setRangePercent(clamped);
            if (onRangeChange && startDate && endDate && totalMs) {
                const startMs = new Date(startDate).getTime();
                const startTime = new Date(startMs + (clamped[0] / 100) * totalMs);
                const endTime = new Date(startMs + (clamped[1] / 100) * totalMs);
                onRangeChange({ startTime, endTime, percent: clamped });
            }
        },
        [onRangeChange, startDate, endDate, totalMs, clampRange, rangePercent]
    );

    if (!events.length) {
        return (
            <div className="trails-trip-timeline trails-trip-timeline--empty">
                <p className="timeline-empty-message">{messages['realtimemaps_noData'] || 'No hay datos'}</p>
            </div>
        );
    }

    return (
        <div data-testid="trails-trip-timeline" className="trails-trip-timeline">
            <div className="trails-trip-timeline__header">
                <span className="trails-trip-timeline__time" aria-label="Hora inicial">
                    {formatTime(startDate)}
                </span>
                <div className="trails-trip-timeline__track-wrap">
                    <div className="trails-trip-timeline__track" role="presentation">
                        {segments.map((seg, idx) => (
                            <div
                                key={idx}
                                className="trails-trip-timeline__segment"
                                style={{
                                    left: `${seg.left}%`,
                                    width: `${seg.width}%`,
                                    backgroundColor: seg.color,
                                }}
                            />
                        ))}
                    </div>
                    <div className="trails-trip-timeline__video-events" aria-hidden="true">
                        {videoEventsOnTimeline.map(({ id, percent, icon, name, color }) => (
                            <Tooltip key={id} title={name}>
                                <span
                                    className="trails-trip-timeline__video-event-icon"
                                    style={{ left: `${percent}%` }}
                                >
                                    <TimelineEventIcon url={icon} name={name} color={color} />
                                </span>
                            </Tooltip>
                        ))}
                    </div>
                    <div className="trails-trip-timeline__slider-clip">
                        <TrailsTripTimelineSlider
                            value={rangePercent}
                            onChange={handleRangeChange}
                            valueLabelDisplay="off"
                            min={0}
                            max={100}
                            step={0.5}
                            disableSwap
                            sx={{ margin: 0, padding: 0 }}
                        />
                    </div>
                </div>
                <span className="trails-trip-timeline__time" aria-label="Hora final">
                    {formatTime(endDate)}
                </span>
                <Tooltip title={messages['realtimemaps_vehicletrails_requestvideo'] || 'Solicitar clip'}>
                    <span>
                        <IconButton
                            className="trails-trip-timeline__upload-btn"
                            onClick={handleRequestClip}
                            disabled={!selectedTimeRange || !deviceId}
                            size="small"
                            aria-label={messages['realtimemaps_vehicletrails_requestvideo'] || 'Solicitar clip'}
                        >
                            <UploadIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </div>
            <div className="trails-trip-timeline__scale-row">
                <span className="trails-trip-timeline__scale-spacer" aria-hidden="true">
                    {formatTime(startDate)}
                </span>
                <div className="trails-trip-timeline__scale-wrap">
                    {timeScaleTicks.map((tick, idx) => (
                        <div
                            key={idx}
                            className="trails-trip-timeline__scale-tick"
                            style={{ left: `${tick.percent}%` }}
                        >
                            <span className="trails-trip-timeline__scale-tick-line" />
                            <span className="trails-trip-timeline__scale-tick-label">
                                {formatScaleTime(tick.time)}
                            </span>
                        </div>
                    ))}
                </div>
                <span className="trails-trip-timeline__scale-spacer" aria-hidden="true">
                    {formatTime(endDate)}
                </span>
            </div>
        </div>
    );
});

TrailsTripTimeline.propTypes = {
    mapRef: PropTypes.object,
    trailsFilterEvents: PropTypes.array,
    onRangeChange: PropTypes.func,
};

TrailsTripTimeline.defaultProps = {
    mapRef: null,
    trailsFilterEvents: [],
    onRangeChange: undefined,
};

export default TrailsTripTimeline;
